import React, { useContext, useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'

import {
  requestConfirmState,
  requestApproveEdits,
  requestDelistState,
  requestFinishProposeSite,
  requestNominateSite,
  requestReviewByRfp,
  requestCommentsData,
  deleteSiteRequest,
  requestDoesNotQualify,
  requestFinishEditOnlySite,
  deleteEditSiteOnlyRequest
} from 'api/site'
import Button from 'components/Button'
import CommentsIcon from 'assets/icons/comments-icon'
import SubmitIcon from 'assets/icons/submit-icon'
import ConfirmIcon from 'assets/icons/confirm-icon'
import ArrowLeftLine from 'assets/icons/arrow-left-line'
import BlockIcon from 'assets/icons/block-icon'
import ReturnDraftIcon from 'assets/icons/return-draft-icon'
import CheckIcon from 'assets/icons/check-icon'
import RemoveIcon from 'assets/icons/remove-icon16px'
import NominateIcon from 'assets/icons/nominate-icon'
import DelistIcon from 'assets/icons/delist-icon'
import CommentSection from 'flow/siteProposal/components/comments/commentSection'
import { SiteContext } from 'providers/site'
import Modal from 'components/Modal'
import AutoSaveIcon from 'features/AutoSaveIcon'
import { ProfileContext } from 'providers/profile'
import { INDIVIDUAL_ROLE_ID, NCG_ROLE_ID } from 'constants/roles'
import { canReviewSites, canConfirmSites } from 'utils/rolePermissions'
import FlowRequestModal from 'components/FlowRequestModal'
import ValidationProposalModal from './components/validationProposalModal'
import { useRequestQueue } from 'hooks/useRequestQueue'
import { useSiteProposalPermissions } from 'hooks/useSiteProposalPermissions'
import { showErrorToast } from 'utils/toast'

import { FooterButtonDivider, NewSiteFooterButtonWrapper, NewSiteFooterRightPart, NewSiteFooterWrapper } from './style'
import SiteProposalConfirmation from './components/siteProposalConfirmation'
import {
  SITE_STATUS_PROPOSED_ID,
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_REVIEWED_BY_RFP_ID,
  SITE_STATUS_NOMINATED_ID,
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_DOES_NOT_QUALIFY,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
} from 'constants/siteStatuses'
import { MySiteContext } from '../../../../providers/mySiteProvider'
import SiteEditConfirmation from './components/siteEditConfirmation'
import { editOnly } from 'routing/constants'

const areAllInstancesAreValid = (instances) => {
  const areAllInstancesValid = Object.values(instances).every((instance) => {
    const isAllFieldAreValid = Object.values(instance).every((field) => {
      return field === true
    })
    return isAllFieldAreValid
  })

  return areAllInstancesValid
}

const enforceGeometryMustExist = () => {
  return import.meta.env.REACT_APP_ENFORCE_GEOMETRY_MUST_EXIST === 'true'
}

const getUncompletedSection = (validation) => {
  const result = []
  const isSectionValid = (section) => Object.values(section).every((element) => element === true)

  if (!isSectionValid(validation.about)) {
    result.push('About')
  }

  if (!isSectionValid(validation.details)) {
    result.push('Site Details')
  }

  const hasInvalidAssessment =
    !validation.assessmentsAvailable ||
    ['criterionC', 'criterionE', 'ecoSystem', 'species', 'assessmentParameters', 'congregations']
      .some((key) => validation[key] && !areAllInstancesAreValid(validation[key]))

  if (hasInvalidAssessment) {
    result.push('Assessment')
  }

  if (validation.geometry && enforceGeometryMustExist() && !validation.geometry.validPolygon) {
    result.push('Map Page')
  }
  if (validation.geometry?.notConfigured) {
    console.error('Geometry validation not configured')
  }

  return [...new Set(result)]
}

const shouldShowCommentsButton = ({ showComments, siteStatusId, roleId }) => {
  if (!showComments) {
    return false
  }

  if (siteStatusId === SITE_STATUS_EDIT_ONLY_ID) {
    return roleId === INDIVIDUAL_ROLE_ID || roleId === NCG_ROLE_ID
  }

  return true
}

const renderNominateAction = ({ setSiteToNominate }) => (
  <NewSiteFooterButtonWrapper>
    <Button
      type="primary"
      withIcon={true}
      height="40px"
      onClick={() => {
        setSiteToNominate(true)
      }}
    >
      <NominateIcon />
      <FormattedMessage id="nominate" />
    </Button>
  </NewSiteFooterButtonWrapper>
)

const canRenderEditOnlyFooterActions = ({ site, profile, canProposeFromDraft, isLoadingPermissions }) =>
  site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID &&
  (profile.Role.id === INDIVIDUAL_ROLE_ID || profile.Role.id === NCG_ROLE_ID) &&
  canProposeFromDraft &&
  !isLoadingPermissions

const canRenderNominateFooterAction = ({ site, profile, canNominate, isLoadingPermissions }) =>
  site.SiteStatusId === SITE_STATUS_REVIEWED_BY_RFP_ID &&
  (profile.RoleId === INDIVIDUAL_ROLE_ID || profile.RoleId === NCG_ROLE_ID) &&
  canNominate &&
  !isLoadingPermissions

const renderFooterActions = ({
  site,
  profile,
  isEditable,
  openDeleteModal,
  setIsModalOpen,
  getSiteValidation,
  setIsEditOnlyModalOpen,
  getEditOnlySiteValidation,
  setReviewedByRfp,
  setSiteToNominate,
  setSiteToApproveEdits,
  openModalToDoesNotQualify,
  openModalToDelist,
  setSiteToConfirm,
  canProposeFromDraft,
  canNominate,
  isLoadingPermissions
}) => {
  if (site.SiteStatusId === SITE_STATUS_DRAFT_ID && isEditable && canProposeFromDraft && !isLoadingPermissions) {
    return (
      <NewSiteFooterButtonWrapper>
        <Button height="40px" withIcon={true} type="delist" onClick={openDeleteModal}>
          <RemoveIcon />
          <FormattedMessage id="deleteDraftBtn" />
        </Button>
        <Button
          dataTestId="submit-proposal-button"
          height="40px"
          withIcon={true}
          type="primary"
          onClick={() => {
            getSiteValidation().then(() => {
              setIsModalOpen(true)
            })
          }}
        >
          <SubmitIcon />
          <FormattedMessage id="submitProposal" />
        </Button>
      </NewSiteFooterButtonWrapper>
    )
  }

  if (canRenderEditOnlyFooterActions({ site, profile, canProposeFromDraft, isLoadingPermissions })) {
    return (
      <>
        <FooterButtonDivider />
        <NewSiteFooterButtonWrapper>
          <Button height="40px" withIcon={true} type="delist" onClick={openDeleteModal}>
            <RemoveIcon />
            <FormattedMessage id="deleteEdits" />
          </Button>
          <Button
            height="40px"
            withIcon={true}
            type="primary"
            onClick={() => {
              getEditOnlySiteValidation().then(() => {
                setIsEditOnlyModalOpen(true)
              })
            }}
          >
            <SubmitIcon />
            <FormattedMessage id="submitEdits" />
          </Button>
        </NewSiteFooterButtonWrapper>
      </>
    )
  }

  if (site.SiteStatusId === SITE_STATUS_PROPOSED_ID && canReviewSites(profile.RoleId)) {
    return (
      <>
        <FooterButtonDivider />
        <NewSiteFooterButtonWrapper>
          <Button
            height="40px"
            withIcon={true}
            type="error"
            onClick={() => {
              setReviewedByRfp({ isApproved: false })
            }}
          >
            <ReturnDraftIcon />
            <FormattedMessage id="returnToDraft" />
          </Button>
          <Button
            height="40px"
            withIcon={true}
            type="primary"
            onClick={() => {
              setReviewedByRfp({ isApproved: true })
            }}
          >
            <CheckIcon />
            <FormattedMessage id="approve" />
          </Button>
        </NewSiteFooterButtonWrapper>
      </>
    )
  }

  if (canRenderNominateFooterAction({ site, profile, canNominate, isLoadingPermissions })) {
    return renderNominateAction({ setSiteToNominate })
  }

  if (site.SiteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID && (canConfirmSites(profile.RoleId) || canReviewSites(profile.RoleId))) {
    return (
      <>
        <FooterButtonDivider />
        <NewSiteFooterButtonWrapper>
          <Button
            type="error"
            height="40px"
            withIcon={true}
            onClick={() => {
              return setSiteToApproveEdits({ ...site, isApproved: false })
            }}
          >
            <BlockIcon />
            <FormattedMessage id="rejectEdits" />
          </Button>
          <Button
            type="primary"
            height="40px"
            withIcon={true}
            onClick={() => {
              return setSiteToApproveEdits({ ...site, isApproved: true })
            }}
          >
            <ConfirmIcon />
            <FormattedMessage id="approveEdits" />
          </Button>
        </NewSiteFooterButtonWrapper>
      </>
    )
  }

  if (site.SiteStatusId === SITE_STATUS_NOMINATED_ID && canConfirmSites(profile.RoleId)) {
    return (
      <>
        {!site.reassessmentFlag && (
          <>
            <FooterButtonDivider />
            <NewSiteFooterButtonWrapper>
              <Button type="delist" height="40px" withIcon={true} onClick={openModalToDoesNotQualify}>
                <DelistIcon />
                <FormattedMessage id="doesNotQualify" />
              </Button>
            </NewSiteFooterButtonWrapper>
          </>
        )}
        <FooterButtonDivider />
        {site.delist && (
          <NewSiteFooterButtonWrapper style={{ marginRight: '15px' }}>
            <Button type="delist" height="40px" withIcon={true} onClick={openModalToDelist}>
              <DelistIcon />
              <FormattedMessage id="delist" />
            </Button>
          </NewSiteFooterButtonWrapper>
        )}
        <NewSiteFooterButtonWrapper>
          <Button
            type="error"
            height="40px"
            withIcon={true}
            onClick={() => {
              return setSiteToConfirm({ ...site, isApproved: false })
            }}
          >
            <BlockIcon />
            <FormattedMessage id="rejected" />
          </Button>
          <Button
            type="primary"
            height="40px"
            withIcon={true}
            onClick={() => {
              return setSiteToConfirm({ ...site, isApproved: true })
            }}
          >
            <ConfirmIcon />
            <FormattedMessage id="confirm" />
          </Button>
        </NewSiteFooterButtonWrapper>
      </>
    )
  }

  if (site.SiteStatusId === SITE_STATUS_DRAFT_ID && canConfirmSites(profile.RoleId) && site.reassessmentFlag) {
    return (
      <NewSiteFooterButtonWrapper>
        <Button height="40px" withIcon={true} type="delist" onClick={openDeleteModal}>
          <RemoveIcon />
          <FormattedMessage id="deleteDraftBtn" />
        </Button>
      </NewSiteFooterButtonWrapper>
    )
  }

  return null
}

const Footer = () => {
  const history = useNavigate()
  const params = useParams()
  const { siteProposalFieldValidation, getSiteValidation, site, getMySite, isEditable, getEditOnlySiteValidation } = useContext(SiteContext)
  const { getSitesCount } = useContext(MySiteContext)
  const { profile } = useContext(ProfileContext)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditOnlyModalOpen, setIsEditOnlyModalOpen] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [reviewedByRfp, setReviewedByRfp] = useState(false)
  const [siteToNominate, setSiteToNominate] = useState(false)
  const [siteToConfirm, setSiteToConfirm] = useState(false)
  const [siteToApproveEdits, setSiteToApproveEdits] = useState(false)
  const [isSiteToDelist, setIsSiteToDelist] = useState(false)
  const [isSiteDoesNotQualified, setIsSiteDoesNotQualified] = useState(false)
  const [commentsNumber, setCommentsNumber] = useState(0)
  const [isModalForDeletingOpen, setIsModalForDeletingOpen] = useState(false)
  const { permissions, isLoadingPermissions, refreshPermissions } = useSiteProposalPermissions()
  const isNcgUser = profile.RoleId === NCG_ROLE_ID
  const sections = getUncompletedSection(siteProposalFieldValidation)
  const isLoading = useRequestQueue()
  const showComments = (site.SiteStatusId !== SITE_STATUS_DRAFT_ID && site.SiteStatusId !== SITE_STATUS_CONFIRMED_ID && site.SiteStatusId !== SITE_STATUS_DOES_NOT_QUALIFY) || site.isApproved === false

  const editOnlySite = site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID || site.SiteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID ? editOnly : null;
  const editOnlySiteStatus = site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID || site.SiteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  const historyId = editOnlySiteStatus ? site.sitehistoryId : params.siteHistoryId


  function goBack() {
    history('/sites')
    getSitesCount(true)
  }

  function openDeleteModal() {
    setIsModalForDeletingOpen(true)
  }

  function closeDelistModal() {
    setIsSiteToDelist(false)
  }

  function closeDoesNotQualifyModal() {
    setIsSiteDoesNotQualified(false)
  }

  function openModalToDelist() {
    setIsSiteToDelist(true)
  }

  function openModalToDoesNotQualify() {
    setIsSiteDoesNotQualified(true)
  }
  async function deleteSite() {
    try {
      let data

      if (site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID && site.historyId) {
        data = await deleteEditSiteOnlyRequest(site.id, site.SiteStatusId)
      } else {
        data = await deleteSiteRequest(site.id, site.SiteStatusId, site.reassessmentFlag, site.historyId)
      }

      if (data?.error) {
        return showErrorToast(data.error, 'showBorder')
      }

      goBack()
    } catch (err) {
      showErrorToast(err, 'showBorder');
    }
  }

  async function delistSite() {
    try {
      const data = await requestDelistState(site.historyId, true);
      if (data.error) {
        return showErrorToast(data.error, 'showBorder');
      }
      goBack();
    } catch (error) {
      showErrorToast(error, 'showBorder');
    }
  }

  async function doesNotQualify() {
    try {
      const data = await requestDoesNotQualify(site.id);
      if (data.error) {
        return showErrorToast(data.error, 'showBorder');
      }
      goBack();
    } catch (error) {
      showErrorToast(error);
    }
  }

  async function proposeSite() {
    try {
      if (!isNcgUser) {
        const access = await refreshPermissions(site.id, params.siteHistoryId)
        if (!access.canProposeFromDraft) {
          showErrorToast('Only the proposer or a co-proposer can submit this draft.', 'showBorder')
          return
        }
      }
      const response = await requestFinishProposeSite(site.id, params.siteHistoryId);
      if (response.success) {
        await getMySite();
      }
    } catch (error) {
      showErrorToast(error, 'showBorder');
    }
  }

  async function rfpAction(element, isApproved) {
    try {
      await requestReviewByRfp(element.id, params.siteHistoryId, isApproved);
      setReviewedByRfp(false);
      goBack();
    } catch (error) {
      showErrorToast(error, 'showBorder');
    }
  }

  async function nominateSite(element) {
    try {
      if (!isNcgUser) {
        const access = await refreshPermissions(element.id, params.siteHistoryId)
        if (!access.canNominate) {
          showErrorToast('Only the proposer or a co-proposer can nominate this site.', 'showBorder')
          setSiteToNominate(false)
          return
        }
      }
      await requestNominateSite(element.id, params.siteHistoryId);
      setSiteToNominate(false);
      await getMySite();
    } catch (error) {
      showErrorToast(error, 'showBorder');
    }
  }

  async function confirmAction(element, isApproved) {
    try {
      await requestConfirmState(element.id, params.siteHistoryId, isApproved);
      setSiteToConfirm(false);
      goBack();
    } catch (error) {
      showErrorToast(error, 'showBorder');
    }
  }

  async function approveEditsAction(element, isApproved) {
    try {
      await requestApproveEdits(element.id, historyId, isApproved);
      setSiteToApproveEdits(false);
      goBack();
    } catch (error) {
      showErrorToast(error, 'showBorder');
    }
  }

  async function getCommentsData() {
    if (showComments) {
      try {
        const data = await requestCommentsData(site.id, historyId, editOnlySite);
        if (data.error) return;
        setCommentsNumber(data.length);
      } catch (error) {
        showErrorToast(error, 'showBorder');
      }
    }
  }

  const proposeEditSite = async () => {
    try {
      if (!isNcgUser) {
        const permissionSiteId = site?.siteId || site?.id
        const access = await refreshPermissions(permissionSiteId)
        if (!access.canProposeFromDraft) {
          showErrorToast('Only the proposer or a co-proposer can submit edits for this site.', 'showBorder')
          return
        }
      }
      const response = await requestFinishEditOnlySite(site.id)
      if (response.success) {
        getMySite()
      }
    } catch (error) {
      showErrorToast(error, 'showBorder');
    }
  }

  useEffect(() => {
    getCommentsData()
  }, [])

  useEffect(() => {
    refreshPermissions(site?.id, params.siteHistoryId)
  }, [site?.id, params.siteHistoryId, refreshPermissions])

  return (
    <NewSiteFooterWrapper>
      <div style={{ width: '35%' }}>
        <NewSiteFooterButtonWrapper onClick={goBack}>
          <Button height="40px" withIcon={true}>
            <ArrowLeftLine />
            <FormattedMessage id="backToSites" />
          </Button>
        </NewSiteFooterButtonWrapper>
      </div>
      {site.SiteStatusId === SITE_STATUS_DRAFT_ID && <AutoSaveIcon loading={isLoading} time={'saved'} />}
      <NewSiteFooterRightPart>
        <NewSiteFooterButtonWrapper>
          {shouldShowCommentsButton({
            showComments,
            siteStatusId: site.SiteStatusId,
            roleId: profile.Role.id
          }) && (
            <Button
              type="secondary"
              height="40px"
              onClick={() => {
                setIsOpen(true)
              }}
              withIcon={true}
            >
              <CommentsIcon />
              <FormattedMessage id="Comments" /> ({commentsNumber})
            </Button>
          )}
        </NewSiteFooterButtonWrapper>
        {renderFooterActions({
          site,
          profile,
          isEditable,
          openDeleteModal,
          setIsModalOpen,
          getSiteValidation,
          setIsEditOnlyModalOpen,
          getEditOnlySiteValidation,
          setReviewedByRfp,
          setSiteToNominate,
          setSiteToApproveEdits,
          openModalToDoesNotQualify,
          openModalToDelist,
          setSiteToConfirm,
          canProposeFromDraft: isNcgUser || permissions.canProposeFromDraft,
          canNominate: isNcgUser || permissions.canNominate,
          isLoadingPermissions
        })}
      </NewSiteFooterRightPart>
      <Modal
        width="416px"
        toggleModal={isEditOnlyModalOpen ? setIsEditOnlyModalOpen : setIsModalOpen}
        isOpen={(isModalOpen || isEditOnlyModalOpen) && sections.length > 0}
        modalTitleId="unfinishedProposal"
      >
        <ValidationProposalModal sections={sections} toggleModal={isEditOnlyModalOpen ? setIsEditOnlyModalOpen : setIsModalOpen} />
      </Modal>
      <Modal
        width="416px"
        toggleModal={setIsModalOpen}
        isOpen={isModalOpen && sections.length === 0}
        modalTitleId="Propose assessment?"
      >
        <SiteProposalConfirmation name={site.name} proposeSite={proposeSite} toggleModal={setIsModalOpen} />
      </Modal>

      <Modal
        width="416px"
        toggleModal={() => setIsEditOnlyModalOpen(false)} // Ensures modal can be toggled
        isOpen={isEditOnlyModalOpen && sections.length === 0}
        modalTitleId="Submit Edits?"
      >
        <SiteEditConfirmation
          proposeEditSite={proposeEditSite}
          name={site.name}
          toggleModal={() => setIsEditOnlyModalOpen(false)} // Ensure it closes when needed
        />
      </Modal>

      <Modal
        width="480px"
        height="94vh"
        isOpen={isOpen}
        toggleModal={setIsOpen}
        modalTitleId="commentBox"
        titleValue={commentsNumber === 0 ? '0' : commentsNumber}
        infoTooltip="commentsHint"
        zIndex="1070"
        label="leaveComment"
        padding="0"
        bottomBorder={false}
        paddingBottom="0"
        sideModal={true}
      >
        <CommentSection setCommentsNumber={setCommentsNumber} />
      </Modal>
      <Modal
        width="416px"
        isOpen={reviewedByRfp.isApproved}
        toggleModal={setReviewedByRfp}
        modalTitleId="approveForNomination"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setReviewedByRfp(false)
          }}
          confirmHandler={rfpAction}
          confirmBtnText="approve"
          text={<FormattedMessage id="approveForNominationText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={true}
        />
      </Modal>
      <Modal
        width="416px"
        isOpen={reviewedByRfp.isApproved === false}
        toggleModal={setReviewedByRfp}
        modalTitleId="returnDraft"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setReviewedByRfp(false)
          }}
          confirmHandler={rfpAction}
          confirmBtnText="returnToDraft"
          text={<FormattedMessage id="returnFeedbackText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={false}
        />
      </Modal>
      <Modal width="416px" isOpen={siteToNominate} toggleModal={setSiteToNominate} modalTitleId="nominateAssessment">
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToNominate(false)
          }}
          confirmHandler={nominateSite}
          confirmBtnText="nominate"
          text={<FormattedMessage id="nominateSiteText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={true}
        />
      </Modal>
      <Modal
        width="416px"
        isOpen={siteToConfirm.isApproved === true}
        toggleModal={setSiteToConfirm}
        modalTitleId="confirmAssessment"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToConfirm(false)
          }}
          confirmHandler={confirmAction}
          confirmBtnText="confirm"
          text={<FormattedMessage id="confirmAssessmentText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={true}
        />
      </Modal>
      <Modal
        width="416px"
        isOpen={siteToApproveEdits.isApproved === true}
        toggleModal={setSiteToApproveEdits}
        modalTitleId="approveEdits"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToApproveEdits(false)
          }}
          confirmHandler={approveEditsAction}
          confirmBtnText="confirm"
          text={<FormattedMessage id="approveEditsText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={true}
        />
      </Modal>
      <Modal
        width="416px"
        isOpen={siteToApproveEdits.isApproved === false}
        toggleModal={setSiteToApproveEdits}
        modalTitleId="rejectEdits"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToApproveEdits(false)
          }}
          confirmHandler={approveEditsAction}
          confirmBtnText="rejectBtn"
          text={<FormattedMessage id="rejectEditsText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={false}
        />
      </Modal>
      <Modal
        width="416px"
        isOpen={siteToConfirm.isApproved === false}
        toggleModal={setSiteToConfirm}
        modalTitleId="rejectAssessment"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToConfirm(false)
          }}
          confirmHandler={confirmAction}
          confirmBtnText="rejectBtn"
          text={<FormattedMessage id="rejectAssessmentText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={false}
        />
      </Modal>
      <Modal width="496px" isOpen={isSiteToDelist} toggleModal={closeDelistModal} modalTitleId="delistAssessment">
        <FlowRequestModal
          cancelHandler={closeDelistModal}
          confirmHandler={delistSite}
          confirmBtnText="delistBtn"
          text={<FormattedMessage id="delistAssessmentText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={false}
        />
      </Modal>
      <Modal
        width="496px"
        isOpen={isSiteDoesNotQualified}
        toggleModal={closeDoesNotQualifyModal}
        modalTitleId="doesNotQualifyModal"
      >
        <FlowRequestModal
          cancelHandler={closeDoesNotQualifyModal}
          confirmHandler={doesNotQualify}
          confirmBtnText="confirm"
          text={<FormattedMessage id="doesNotQualifyText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={true}
        />
      </Modal>
      <Modal
        width="496px"
        isOpen={isModalForDeletingOpen}
        toggleModal={setIsModalForDeletingOpen}
        modalTitleId={site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID ? 'deleteEditSiteOnly' : 'deleteDraft'}
      >
        <FlowRequestModal
          cancelHandler={() => {
            setIsModalForDeletingOpen(false)
          }}
          confirmHandler={deleteSite}
          confirmBtnText="yesDelete"
          text={
            site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID ? (
              <FormattedMessage id="editSiteForDeletingBody" values={{ siteName: <b>{site.name}</b> }} />
            ) : (
              <FormattedMessage id="siteForDeletingBody" values={{ siteName: <b>{site.name}</b> }} />
            )
          }
          item={site}
          approve={false}
        />
      </Modal>
    </NewSiteFooterWrapper>
  )
}

export default Footer
